/**
 * User Presentations Service
 * 
 * Handles fetching and managing user presentation data from Firestore
 */

// Fetch presentations via cloud function using email-based lookup
import { auth } from '../firebase';
import syncUserByEmailService from './sync-user-by-email-service';
import * as localStorageService from './local-storage-service';

/**
 * Fetch all presentations for the current user
 * @returns {Promise<Array>} Array of presentation objects
 */
export const getUserPresentations = async (debug = false) => {
  try {
    // Obtain user email from local storage first, fallback to auth object
    const storedUserData = localStorageService.getUserData(false);
    const email = storedUserData?.email || auth.currentUser?.email;

    if (!email) {
      throw new Error('User email not found');
    }

    const functionsUrl = syncUserByEmailService.getFunctionsUrl();

    const res = await fetch(`${functionsUrl}/getUserPresentations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    const result = await res.json();

    if (!res.ok || result.status !== 'success') {
      throw new Error(result.message || 'Failed to fetch presentations');
    }

    // Cloud function returns ISO timestamps; convert to Date objects for UI
    const presentations = (result.data || []).map((p) => ({
      ...p,
      timestamp: p.timestamp ? new Date(p.timestamp) : new Date()
    }));

    return presentations;
  } catch (error) {
    console.error('Error fetching user presentations:', error);
    throw error;
  }
};

/**
 * Format a presentation date for display
 * @param {Date} date - The presentation timestamp
 * @returns {String} Formatted date string
 */
export const formatPresentationDate = (date) => {
  if (!date) return 'Unknown date';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
